import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const drawerWidth = 240;

const LessonEdit = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [lessonName, setLessonName] = useState("");
  const [lessonContent, setLessonContent] = useState("");
  const [lessonFile, setLessonFile] = useState(null);
  const [lessonFilePath, setLessonFilePath] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const fileInputRef = useRef(null);

  // Fetch Teachers
  const fetchTeachers = async () => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/GetTeacher",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
        }
      );
      const data = await response.json();
      setTeachers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setSnackbarMessage("Error fetching teachers");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // Fetch Classes
  const fetchClasses = async (teacherId) => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/GetClass",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({ teacherId }),
        }
      );
      const data = await response.json();
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      setSnackbarMessage("Error fetching classes");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // Fetch Sections
  const fetchSections = async (classId) => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher/ddlSection_clsId",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            teacherId: selectedTeacher,
            classId,
          }),
        }
      );
      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSnackbarMessage("Error fetching sections");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // Fetch Subjects
  const fetchSubjects = async (classId, sectionId, teacherId) => {
    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/AssignmentCreate/Subjectddl",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({ classId, sectionId, teacherId }),
        }
      );
      const data = await response.json();
      setSubjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSnackbarMessage("Error fetching subjects");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // Fetch lesson data on component mount
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const response = await fetch(
          "https://wsms.aidigitalsec.com/api/Teacher_Lessoon/GetLesson_Teacher",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
            body: JSON.stringify({}),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const lessonToEdit = data.find((lesson) => lesson.lessonId === parseInt(lessonId));
          if (lessonToEdit) {
            setSelectedTeacher(lessonToEdit.teacherId || sessionStorage.getItem("employeeId"));
            setSelectedClass(lessonToEdit.classId);
            setSelectedSection(lessonToEdit.sectionId);
            setSelectedSubject(lessonToEdit.subId);
            setLessonName(lessonToEdit.lessonName);
            setLessonContent(lessonToEdit.lessonContent);
            setLessonFilePath(lessonToEdit.lessionPdfUrl);
          } else {
            setSnackbarMessage("Lesson not found.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
          }
        } else {
          setSnackbarMessage("Failed to fetch lesson details.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        }
      } catch (error) {
        console.error("Error fetching lessons:", error);
        setSnackbarMessage("Error fetching lesson details.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    };

    fetchLessons();
    fetchTeachers();
  }, [lessonId]);

  // Handle Save Lesson
  const handleSaveLesson = async () => {
    if (!selectedClass || !selectedSection || !selectedSubject || !lessonName || !lessonContent || !lessonFilePath) {
      setError("All fields are required, including the lesson file.");
      return;
    }

    setError("");

    const lessonData = {
      classId: selectedClass,
      subId: selectedSubject,
      lessonName,
      lessonContent,
      lessionPdfUrl: lessonFilePath,
    };

    try {
      const response = await fetch(
        `https://wsms.aidigitalsec.com/api/Teacher_Lessoon/Id?Id=${lessonId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(lessonData),
        }
      );

      if (response.ok) {
        setSnackbarMessage("Lesson updated successfully");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else {
        setError("Failed to save the lesson.");
      }
    } catch (error) {
      console.error("Error saving lesson:", error);
      setError("Error saving lesson.");
    }
  };

  // Handle File Change
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("File size must be less than 2 MB.");
      return;
    }

    setLessonFile(file);
    await uploadLessonFile(file);
  };

  // Upload Lesson File
  const uploadLessonFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("teacherId", sessionStorage.getItem("employeeId"));

    try {
      const response = await fetch(
        "https://wsms.aidigitalsec.com/api/Teacher_Lessoon/LessonFileUpload",
        {
          method: "POST",
          headers: {
            Authorization: sessionStorage.getItem("token"),
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLessonFilePath(data.filePath || "");
        setError("");
      } else {
        setError("Failed to upload the file.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("Error uploading file.");
    }
  };

  // Clear Form
  const clearForm = () => {
    setSelectedTeacher("");
    setSelectedClass("");
    setSelectedSection("");
    setSelectedSubject("");
    setLessonName("");
    setLessonContent("");
    setLessonFile(null);
    setLessonFilePath("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    clearForm();
    navigate(-1);
  };

  // Effects
  useEffect(() => {
    if (selectedTeacher) {
      fetchClasses(selectedTeacher);
    } else {
      setClasses([]);
      setSections([]);
      setSubjects([]);
    }
  }, [selectedTeacher]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
    } else {
      setSections([]);
      setSubjects([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSection) {
      fetchSubjects(selectedClass, selectedSection, selectedTeacher);
    } else {
      setSubjects([]);
    }
  }, [selectedSection]);

  const isFormValid =
    selectedClass && selectedSection && selectedSubject && lessonName && lessonContent && lessonFilePath;

  return (
    <Box component="main" sx={{
  width: { md: `calc(100% - ${drawerWidth}px)` },
  ml: { md: `${drawerWidth}px` },
  mt: { xs: 0, sm: '64px' },
  p: { xs: 1, md: 3 },
  background: "linear-gradient(135deg, #f5f5f5, #ffffff)",
  minHeight: "100vh",
}}>

     <Card
  sx={{
    borderRadius: 4,
    boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
    background: "linear-gradient(180deg, #f8fafc, #ffffff)",
  }}
>

        <CardContent>
          <Box
  sx={{
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    borderRadius: 2,
    py: 2.5,
    px: 4,
    textAlign: "center",
    mb: 4,
  }}
>
  <Typography
    variant="h4"
    sx={{
      color: "#000000",
      fontWeight: 700,
      fontSize: { xs: "1.75rem", md: "2.2rem" },
      textShadow: "1px 1px 3px rgba(100,98,98,0.3)",
    }}
  >
    Edit Lesson
  </Typography>
</Box>


          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Select Teacher</InputLabel>
                <Select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  label="Select Teacher"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Teacher</MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                      {teacher.employeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedTeacher}>
                <InputLabel>Select Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label="Select Class"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedClass}>
                <InputLabel>Select Section</InputLabel>
                <Select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  label="Select Section"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec.sectionId} value={sec.sectionId}>
                      {sec.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedSection}>
                <InputLabel>Select Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label="Select Subject"
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="">Select Subject</MenuItem>
                  {subjects.map((sub) => (
                    <MenuItem key={sub.subjectId} value={sub.subjectId}>
                      {sub.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Lesson Name
                </Typography>
                <input
  type="text"
  value={lessonName}
  onChange={(e) => setLessonName(e.target.value)}
  placeholder="Enter Lesson Name"
  style={{
    padding: "10px",
    width: "100%",
    borderRadius: "6px",
    border: "1px solid #e5e7eb",
    outline: "none",
  }}
  onFocus={(e) => (e.target.style.border = "1px solid #d97706")}
  onBlur={(e) => (e.target.style.border = "1px solid #e5e7eb")}
/>

              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Lesson Content
                </Typography>
              <textarea
  value={lessonContent}
  onChange={(e) => setLessonContent(e.target.value)}
  placeholder="Enter Lesson Content"
  style={{
    padding: "10px",
    width: "100%",
    borderRadius: "6px",
    border: "1px solid #e5e7eb",
    minHeight: "100px",
    outline: "none",
  }}
  onFocus={(e) => (e.target.style.border = "1px solid #d97706")}
  onBlur={(e) => (e.target.style.border = "1px solid #e5e7eb")}
/>

              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Lesson File (Max 2 MB, PDF only)
                </Typography>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                {lessonFilePath && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Current File:{" "}
                    <a
                      href={`https://arizshad-002-site5.ktempurl.com/${lessonFilePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {lessonFilePath.split("/").pop()}
                    </a>
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
           <Button
  variant="contained"
  onClick={handleSaveLesson}
  disabled={!isFormValid}
  sx={{
    width: "200px",
    textTransform: "none",
    fontWeight: 600,
    background: "linear-gradient(145deg, #f59e0b 0%, #d97706 100%)",
    boxShadow: "0 6px 16px rgba(217,119,6,0.35)",
    "&:hover": {
      background: "linear-gradient(145deg, #d97706 0%, #b45309 100%)",
      boxShadow: "0 8px 20px rgba(217,119,6,0.45)",
    },
  }}
>
  Update Lesson
</Button>

<Button
  variant="outlined"
  onClick={handleCancel}
  sx={{
    width: "150px",
    borderColor: "#d97706",
    color: "#d97706",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#fff7ed",
      borderColor: "#b45309",
    },
  }}
>
  <ArrowBackIcon sx={{ mr: 1 }} />
  Go Back
</Button>

          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            backgroundColor: snackbarSeverity === "success" ? "#4CAF50" : undefined,
            color: snackbarSeverity === "success" ? "white" : undefined,
            "& .MuiAlert-icon": {
              color: snackbarSeverity === "success" ? "white" : undefined,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LessonEdit;